/**
 * Cart API shim
 * Provides the Shopify Ajax Cart endpoints (/cart.js, /cart/update.js, ...)
 * that the CDN scripts call, backed by the headless cart the SDK knows about.
 */

import type { AnalyzifyCart } from '../types/products';

// Internal cart state
let cartState: AnalyzifyCart = {
  items: [],
  total_price: 0,
  item_count: 0,
  token: '',
  currency: 'USD',
  attributes: {},
};

/**
 * Writes cart attributes to the real (Storefront API) cart.
 * Returns the cart's attributes after the write, or null when there is no
 * cart to write to. `keepalive` marks a write made while the page may be
 * unloading (the CDN's keepalive fetch): it must be a single request.
 */
export type CartAttributeWriter = (
  attributes: Record<string, unknown>,
  options: { keepalive: boolean }
) => Promise<Record<string, string> | null>;

let attributeWriter: CartAttributeWriter | null = null;
let installed = false;

/**
 * Shopify Ajax Cart endpoints (optionally under a locale prefix such as
 * /en-gb). Matched on same-origin URLs only, so host routes like
 * /api/cart/add are never intercepted.
 */
const CART_READ_PATH = /^(?:\/[a-z]{2}(?:-[a-z]{2})?)?\/cart\.(?:js|json)$/i;
const CART_WRITE_PATH = /^(?:\/[a-z]{2}(?:-[a-z]{2})?)?\/cart\/(add|update|change|clear)(?:\.js|\.json)?$/i;

function matchCartEndpoint(url: string): { kind: 'read' } | { kind: 'write'; action: string } | null {
  if (typeof window === 'undefined') return null;
  let parsed: URL;
  try {
    parsed = new URL(url, window.location.href);
  } catch {
    return null;
  }
  if (parsed.origin !== window.location.origin) return null;
  if (CART_READ_PATH.test(parsed.pathname)) return { kind: 'read' };
  const write = parsed.pathname.match(CART_WRITE_PATH);
  if (write) return { kind: 'write', action: write[1].toLowerCase() };
  return null;
}

function jsonResponse(body: unknown, status = 200): Response {
  return new Response(JSON.stringify(body), {
    status,
    headers: { 'Content-Type': 'application/json' },
  });
}

/** Read the `attributes` object from an Ajax cart update body (JSON). */
function parseUpdateAttributes(body: unknown): Record<string, unknown> | null {
  if (typeof body !== 'string' || !body) return null;
  try {
    const parsed = JSON.parse(body);
    const attributes = parsed?.attributes;
    return attributes && typeof attributes === 'object' ? attributes : null;
  } catch {
    return null;
  }
}

async function handleCartUpdate(
  attributes: Record<string, unknown> | null,
  keepalive = false
): Promise<Response> {
  if (!attributes || !attributeWriter) {
    // Nothing to forward: answer like Shopify would for a no-op update.
    return jsonResponse(cartState);
  }
  try {
    const written = await attributeWriter(attributes, { keepalive });
    if (written) {
      cartState = { ...cartState, attributes: written };
    }
    return jsonResponse(cartState);
  } catch (error) {
    return jsonResponse({ status: 502, message: String(error) }, 502);
  }
}

/**
 * Install cart API shim
 * Creates a fetch interceptor for the Ajax cart endpoints. Installed once.
 */
export function installCartApiShim(options: { currency?: string } = {}): void {
  if (typeof window === 'undefined') return;

  if (options.currency && !cartState.token) {
    cartState = { ...cartState, currency: options.currency };
  }

  if (installed) return;
  installed = true;

  const originalFetch = window.fetch;

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    const endpoint = matchCartEndpoint(url);

    if (!endpoint) {
      return originalFetch(input, init);
    }

    if (endpoint.kind === 'read') {
      return jsonResponse(cartState);
    }

    if (endpoint.action === 'update') {
      let body: unknown = init?.body;
      if (body === undefined && typeof Request !== 'undefined' && input instanceof Request) {
        try {
          body = await input.clone().text();
        } catch {
          body = undefined;
        }
      }
      return handleCartUpdate(parseUpdateAttributes(body), init?.keepalive === true);
    }

    // add / change / clear: cart mutations in headless go through the
    // Storefront API in the host app — answer with the current state.
    return jsonResponse(cartState);
  };

  // The CDN tries navigator.sendBeacon('/cart/update.js', ...) when the page
  // is being hidden. A beacon cannot be forwarded to the Storefront API, so
  // report it as not queued: the CDN then falls back to its keepalive fetch,
  // which the interceptor above forwards as a single keepalive request.
  if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
    const originalSendBeacon = navigator.sendBeacon.bind(navigator);
    navigator.sendBeacon = (url: string | URL, data?: BodyInit | null): boolean => {
      if (matchCartEndpoint(String(url))) {
        return false;
      }
      return originalSendBeacon(url, data);
    };
  }
}

/**
 * Route the CDN's cart attribute writes (POST /cart/update.js) to the real
 * cart. Pass null to stop forwarding.
 */
export function setCartAttributeWriter(writer: CartAttributeWriter | null): void {
  attributeWriter = writer;
}

/**
 * Update the shim's cart state
 * Call this when cart changes in your headless store
 */
export function updateCartState(cart: AnalyzifyCart): void {
  cartState = { ...cart };

  // Dispatch cart update event
  window.dispatchEvent(
    new CustomEvent('analyzify:cart:updated', {
      detail: { ...cartState },
    })
  );
}

/**
 * Get current cart state
 */
export function getCartState(): AnalyzifyCart {
  return { ...cartState };
}
